import { styles } from "@/reports/reportStyles";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import { useDeckStore } from "@/components/inspection/store/deck";
import { getMinimumOut2OutWidthDisplay } from "@/composables/spanBySpan";

function setDeckDocDefinition(structure) {
  const configStore = useConfigStore();
  const deckStore = useDeckStore();
  const structureUnits = deckStore.getStructureUnits(structure);
  let docDefinition = {
    header: function () {
      return getReportHeader("Deck Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Deck Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Deck Conditions",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "40%",
            text: "B.C.01 Deck Condition Rating: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(REFERENCE_TABLE.CONDITION_RATING, structure?.inspection?.dkRating),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: "Overall Deck Notes: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.OVERALL_DECK_COMMENTS,
        )?.notes,
      },
      {
        columns: [
          {
            width: "40%",
            text: "DK.01 Wearing Surface Condition Rating: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.CONDITION_RATING,
              structure?.userInspection?.deckWsCondRate,
            ),
            margin: [0, 5, 0, 0],
          },
        ],
      },
      {
        text: "Overall Wearing Surface Notes: ",
        bold: true,
        margin: [0, 5, 0, 0],
      },
      {
        text: structure?.inspectionComments?.find(
          (element) => element?.commentType == INSPECTION_COMMENTS.OVERALL_WEARING_SURFACE_COMMENTS,
        )?.notes,
      },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Deck Details",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 0],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.01 NBIS Bridge Length: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.structureBridge?.nbisBridgeLength + " ft",
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.02 Estimated Chloride Content: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.userInspection?.estChlorideContentPct + " %",
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.05 Bridge Width (0/0): ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: getMinimumOut2OutWidthDisplay(structureUnits) + " ft",
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.03 Est. Chloride Content Date: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: getFormattedDateStringNoTime(structure?.userInspection?.estChlorideContentDate),
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.06 Bridge Width (C/C): ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.structureBridge?.curbToCurbWidth + " ft",
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.04 Deck Cracking Metric: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: Number.parseFloat(structure?.userInspection?.deckCrackingMetric).toFixed(2) + " YD/SY",
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.07 Left Curb or Sidewalk Width: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.structureBridge?.lftCurbSw + " ft",
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.05 Protect Install Year: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.userBridge?.protectYear,
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.08 Right Curb or Sidewalk Width: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.structureBridge?.rtCurbSw + " ft",
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.06 Protect Install Note: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.userBridge?.protectNote,
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.10 Bridge Median: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: configStore.getReferenceValue(
              REFERENCE_TABLE.BRIDGE_MEDIAN_TYPE,
              structure?.structureBridge?.bridgeMed,
            ),
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.07 Wearing Surface Thickness: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.userInspection?.wearingSurfaceThickness + " in",
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.13 Maximum Bridge Height: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.userBridge?.maxStructureHeight + " ft",
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.08  Wearing Surface Thck Date: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: getFormattedDateStringNoTime(structure?.userInspection?.wearingSurfaceThicknessDate),
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.15 Irregular Deck Area: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.structureBridge?.irregularDeckArea + " sf",
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.09  Wearing Surface Thck Over: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.userInspection?.wearingSurfaceThicknessOver + " in",
            margin: [5, 3, 0, 0],
          },
        ],
      },
      {
        columns: [
          {
            width: "30%",
            text: "B.G.16 Calculated Deck Area: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.structureBridge?.deckArea + " sf",
            margin: [5, 3, 0, 0],
          },
          {
            width: "30%",
            text: "DK.10  Wearing Surface Thck Under: ",
            bold: true,
            margin: [0, 3, 0, 0],
          },
          {
            width: "*",
            text: structure?.userInspection?.wearingSurfaceThicknessUnder + " in",
            margin: [5, 3, 0, 0],
          },
        ],
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  if (structureUnits?.length) {
    for (let structureUnit of structureUnits) {
      docDefinition.content.push(
        {
          table: {
            headerRows: 0,
            widths: ["33%", "33%", "*"],
            body: [
              [
                {
                  text: "Structure Unit ID: " + structureUnit.strUnitLabel,
                  fillColor: "#ebebeb",
                  alignment: "left",
                  style: ["sectionHeader"],
                },
                {
                  text: `Span Type: ${configStore.getReferenceValue(
                    REFERENCE_TABLE.STRUCTURE_UNIT_TYPE,
                    structureUnit.legacyStructureType,
                  )}`,
                  fillColor: "#ebebeb",
                  alignment: "center",
                  style: ["sectionHeader"],
                },
                {
                  text: "Unit Key: " + structureUnit.strUnitKey,
                  fillColor: "#ebebeb",
                  alignment: "right",
                  style: ["sectionHeader"],
                },
              ],
            ],
          },
          layout: "noBorders",
          margin: [0, 3, 0, 0],
        },
        {
          table: {
            headerRows: 0,
            widths: ["100%"],
            body: [
              [
                {
                  text: "Span " + structureUnit.strUnitLabel + " Details",
                  fillColor: "#ebebeb",
                  alignment: "center",
                  style: ["sectionHeader"],
                },
              ],
            ],
          },
          layout: "noBorders",
          margin: [0, 5, 0, 0],
        },
        {
          columns: [
            {
              width: "30%",
              text: "SU.01 Unit Key: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: structureUnit.strUnitKey,
              margin: [0, 5, 0, 0],
            },
            {
              width: "35%",
              text: "SU.02 Unit ID: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: structureUnit.strUnitLabel,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "50%",
              text: "SU.03 Structure Unit Description: ",
              bold: true,
              margin: [0, 3, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "*",
              text: structureUnit.strUnitDescription,
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "SU.04 Legacy Structure Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURE_TYPE, structureUnit?.legacyStructureType),
              margin: [0, 5, 0, 0],
            },
            {
              width: "35%",
              text: "SP.23 Minimum Span Out-to-Out Width: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: structureUnit?.minSpanOut2OutWidth,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.01 Span Config. Designation: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: structureUnit?.spanConfigDesignation,

              margin: [0, 5, 0, 0],
            },
            {
              width: "35%",
              text: "SP.21 Span Length: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "*",
              text: structureUnit?.spanLength,
              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.08 Deck Interaction: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(REFERENCE_TABLE.DECK_INTERACTION, structureUnit?.deckInteraction),

              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.09 Deck Material and Type: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.DECK_MATERIAL_TYPE,
                structureUnit?.deckMaterialAndType,
              ),

              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.10 Wearing Surface: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(REFERENCE_TABLE.WEARING_SURFACE, structureUnit?.wearingSurface),

              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.11 Deck Prot. System: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.DECK_PROTECTIVE_SYSTEM,
                structureUnit?.deckProtectSystem,
              ),

              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.12 Deck Reinforcing Prot. Sys.: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.DECK_REINFORCING_PROTECTIVE_SYSTEM,
                structureUnit?.deckReinforceProtectSystem,
              ),

              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              width: "30%",
              text: "B.SP.13 Deck Stay-in-Place Forms.: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              width: "25%",
              text: configStore.getReferenceValue(
                REFERENCE_TABLE.DECK_STAY_IN_PLACE_FORMS,
                structureUnit?.deckStayInPlaceForms,
              ),

              margin: [0, 5, 0, 0],
            },
          ],
        },
        {
          table: {
            headerRows: 0,
            widths: ["100%"],
            body: [
              [
                {
                  text: "Span " + structureUnit.strUnitKey + " Inspection Notes",
                  fillColor: "#ebebeb",
                  alignment: "center",
                  style: ["sectionHeader"],
                },
              ],
            ],
          },
          layout: "noBorders",
          margin: [0, 5, 0, 0],
        },
        {
          stack: [
            {
              text: "Deck Top and Wearing Surface: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              text: structure?.structureUnitInspComments?.find(
                (element) =>
                  element?.commentType == INSPECTION_COMMENTS.DECK_TOP_WEARING_SURFACE &&
                  element.structureUnitId == structureUnit.structureUnitId,
              )?.notes,
            },
          ],
          dontBreakRows: true,
        },
        {
          stack: [
            {
              text: "Deck Underside: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              text: structure?.structureUnitInspComments?.find(
                (element) =>
                  element?.commentType == INSPECTION_COMMENTS.DECK_UNDERSIDE &&
                  element.structureUnitId == structureUnit.structureUnitId,
              )?.notes,
            },
          ],
          dontBreakRows: true,
        },
        {
          stack: [
            {
              text: "Deck Drainage: ",
              bold: true,
              margin: [0, 5, 0, 0],
            },
            {
              text: structure?.structureUnitInspComments?.find(
                (element) =>
                  element?.commentType == INSPECTION_COMMENTS.DECK_DRAINAGE &&
                  element.structureUnitId == structureUnit.structureUnitId,
              )?.notes,
            },
          ],
          dontBreakRows: true,
        },
      );
    }
  } else {
    docDefinition.content.push({
      text: "This structure currently contains no structure unit details.",
      bold: true,
      alignment: "center",
      margin: [0, 10, 0, 0],
    });
  }
  return docDefinition;
}

export { setDeckDocDefinition };
